import React, { useState, useRef, useEffect } from "react";
import { ChevronDown, Check } from "lucide-react";
import { cn } from "../../lib/utils";

const SelectContext = React.createContext();

function Select({ children, value, onValueChange, defaultValue, disabled, ...props }) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(value || defaultValue || "");
  const [selectedLabel, setSelectedLabel] = useState("");
  const selectRef = useRef(null);

  useEffect(() => {
    if (value !== undefined) {
      setSelectedValue(value);
    }
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelect = (itemValue, itemLabel) => {
    setSelectedValue(itemValue);
    setSelectedLabel(itemLabel);
    setIsOpen(false);
    if (onValueChange) {
      onValueChange(itemValue);
    }
  };

  const contextValue = {
    selectedValue,
    selectedLabel,
    setSelectedLabel,
    isOpen,
    setIsOpen,
    handleSelect,
    disabled
  };

  return (
    <SelectContext.Provider value={contextValue}>
      <div ref={selectRef} className="relative" {...props}>
        {children}
      </div>
    </SelectContext.Provider>
  );
}

function SelectTrigger({ children, className, ...props }) {
  const context = React.useContext(SelectContext);
  if (!context) {
    throw new Error('SelectTrigger must be used within a Select');
  }

  const { isOpen, setIsOpen, disabled } = context;

  return (
    <button
      type="button"
      role="combobox"
      aria-expanded={isOpen}
      aria-haspopup="listbox"
      disabled={disabled}
      className={cn(
        "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
        className
      )}
      onClick={() => !disabled && setIsOpen(!isOpen)}
      {...props}
    >
      {children}
      <ChevronDown className={cn("h-4 w-4 opacity-50 transition-transform", isOpen && "rotate-180")} />
    </button>
  );
}

function SelectValue({ placeholder, className, ...props }) {
  const context = React.useContext(SelectContext);
  if (!context) {
    throw new Error('SelectValue must be used within a Select');
  }

  const { selectedValue, selectedLabel } = context;

  return (
    <span className={cn("block truncate", className)} {...props}>
      {selectedLabel || selectedValue || placeholder}
    </span>
  );
}

function SelectContent({ children, className, ...props }) {
  const context = React.useContext(SelectContext);
  if (!context) {
    throw new Error('SelectContent must be used within a Select');
  }

  const { isOpen } = context;

  if (!isOpen) return null;

  return (
    <div
      className={cn(
        "absolute top-full left-0 z-50 w-full mt-1 bg-popover border border-border rounded-md shadow-md py-1 animate-in fade-in-80",
        className
      )}
      role="listbox"
      {...props}
    >
      <div className="max-h-60 overflow-auto">
        {children}
      </div>
    </div>
  );
}

function SelectItem({ value, children, className, ...props }) {
  const context = React.useContext(SelectContext);
  if (!context) {
    throw new Error('SelectItem must be used within a Select');
  }

  const { selectedValue, handleSelect } = context;
  const isSelected = selectedValue === value;

  const handleClick = () => {
    const label = typeof children === 'string' ? children : value;
    handleSelect(value, label);
  };

  return (
    <div
      role="option"
      aria-selected={isSelected}
      className={cn(
        "relative flex cursor-pointer items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        isSelected && "bg-accent text-accent-foreground",
        className
      )}
      onClick={handleClick}
      {...props}
    >
      {isSelected && (
        <Check className="mr-2 h-4 w-4" />
      )}
      <span className={cn("block truncate", isSelected && "ml-0", !isSelected && "ml-6")}>
        {children}
      </span>
    </div>
  );
}

// Outros componentes auxiliares
const SelectGroup = ({ children, className, ...props }) => (
  <div className={cn("py-1", className)} {...props}>
    {children}
  </div>
);

const SelectLabel = ({ children, className, ...props }) => (
  <div className={cn("px-2 py-1.5 text-sm font-semibold text-muted-foreground", className)} {...props}>
    {children}
  </div>
);

const SelectSeparator = ({ className, ...props }) => (
  <div className={cn("-mx-1 my-1 h-px bg-border", className)} {...props} />
);

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
}